/**
 * The shape of a sit.
 *
 * One horizontal band: marks where he speaks, ruled space where you sit. It exists
 * because the app's central claim — retained speech stays at recorded speed and in source
 * order — should be visible. Drag the duration and retained marks hold their width while
 * the space between them opens; reviewed omissions appear as a changed set of marks.
 *
 * Drawn to canvas rather than SVG because a 90-minute sit at frequent cadence can carry
 * a couple of dozen segments and this redraws on every frame of a duration drag.
 */

import { useEffect, useRef } from 'preact/hooks';
import type { SessionPlan, Timed } from '../types';

interface Props {
  plan: SessionPlan;
  /** Session seconds elapsed, for the playhead. Omit outside the player. */
  position?: number;
  height?: number;
  /** Show the native duration as a faint underlay, so the change is legible. */
  nativeSec?: number;
  label?: string;
}

export function Timeline({ plan, position, height = 44, nativeSec, label }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const dpr = Math.min(devicePixelRatio || 1, 2);
    const w = cv.clientWidth;
    cv.width = Math.round(w * dpr);
    cv.height = Math.round(height * dpr);
    const g = cv.getContext('2d');
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, height);

    const css = getComputedStyle(cv);
    // Reminders are drawn in the muted tone, not the ink: at full --ink they are the
    // brightest thing on a dark screen, which is the wrong emphasis for the least
    // important marks on the band.
    const ink = css.getPropertyValue('--ink-dim').trim() || '#888';
    const rule = css.getPropertyValue('--rule').trim() || '#444';
    const accent = css.getPropertyValue('--accent-fill').trim() || '#BD7818';
    const dim = css.getPropertyValue('--ink-dim').trim() || '#888';

    const total = plan.actualSec || 1;
    const mid = Math.round(height / 2) + 0.5;
    const x = (t: number) => (t / total) * w;

    // The recording's own length, for comparison. Drawn first and faintly.
    if (nativeSec && nativeSec < total) {
      g.strokeStyle = dim;
      g.globalAlpha = 0.35;
      g.setLineDash([2, 3]);
      g.beginPath();
      g.moveTo(0, height - 3.5);
      g.lineTo(x(nativeSec), height - 3.5);
      g.stroke();
      g.setLineDash([]);
      g.globalAlpha = 1;
    }

    // Silence: a hairline. It is the ground the marks sit on, not an absence.
    g.strokeStyle = rule;
    g.lineWidth = 1;
    g.beginPath();
    g.moveTo(0, mid);
    g.lineTo(w, mid);
    g.stroke();

    // Speech: solid marks. Width is strictly proportional to time, so a 12-minute opening
    // looks like a 12-minute opening and the user can see what they are in for.
    const speechIslands = plan.timeline
      .filter((seg): seg is Extract<Timed, { kind: 'speech' }> => seg.kind === 'speech')
      .map((seg) => seg.island);
    const firstIsland = speechIslands[0];
    const lastIsland = speechIslands.at(-1);
    for (const seg of plan.timeline) {
      if (seg.kind !== 'speech') continue;
      const a = x(seg.startSec);
      const b = x(seg.endSec);
      const isEdge = seg.island === firstIsland || seg.island === lastIsland;
      g.fillStyle = isEdge ? accent : ink;
      const h = isEdge ? height * 0.52 : height * 0.34;
      const bw = Math.max(b - a, 1.5);
      // Rounded, but never more than half the mark's own width — a two-second reminder
      // is 1.5 px wide and a fixed radius would turn it into a dot.
      const r = Math.min(2, bw / 2, h / 2);
      g.beginPath();
      g.roundRect(a, mid - h / 2, bw, h, r);
      g.fill();
    }

    if (position !== undefined && position >= 0) {
      const px = Math.min(x(position), w - 1);
      g.strokeStyle = accent;
      g.lineWidth = 1.5;
      g.beginPath();
      g.moveTo(px + 0.5, 2);
      g.lineTo(px + 0.5, height - 2);
      g.stroke();
    }
  }, [plan, position, height, nativeSec]);

  const spoken = Math.round(plan.preview.spokenSec / 60);
  const longest = Math.round(plan.preview.longestSilenceSec / 60);
  const text = label
    ?? `Session shape: ${spoken} minutes of speech across ${
      plan.timeline.filter((s) => s.kind === 'speech').length
    } passages, longest silence ${longest} minutes.`;

  return (
    <canvas
      ref={ref}
      role="img"
      aria-label={text}
      data-first-speech-sec={plan.timeline.find(s => s.kind === 'speech')?.startSec}
      data-last-speech-sec={plan.timeline.filter(s => s.kind === 'speech').at(-1)?.endSec}
      data-duration-sec={plan.actualSec}
      style={{ width: '100%', height: `${height}px`, display: 'block' }}
    />
  );
}
